import React, { useState, useEffect } from "react";
import AdminShell from "../../components/AdminShell";
import { adminLogout, getAuditLogs } from "../../lib/adminAuth";
import { useNavigate } from "react-router-dom";

const AdminSettings = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const data = await getAuditLogs(20); // Get last 20 logs
      setAuditLogs(data.auditLogs || []);
    } catch (err) {
      console.error("Audit logs fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    if (window.confirm("Are you sure you want to sign out as admin?")) {
      const success = await adminLogout();
      if (success) {
        navigate("/admin/login");
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleString();
  };

  const getActionColor = (action) => {
    if (action.includes("login")) return "bg-green-100 text-green-800";
    if (action.includes("logout")) return "bg-gray-100 text-gray-800";
    if (action.includes("update")) return "bg-blue-100 text-blue-800";
    if (action.includes("bootstrap")) return "bg-purple-100 text-purple-800";
    return "bg-gray-100 text-gray-800";
  };

  // Mock environment values (in real app, these would come from API)
  const envConfig = {
    notificationsConfigured:
      process.env.REACT_APP_NOTIFICATIONS_CONFIGURED === "true",
    billingAlertsMode: process.env.REACT_APP_BILLING_ALERTS_MODE || "TEST",
    appBaseUrl: process.env.REACT_APP_BACKEND_URL || "Unknown",
  };

  return (
    <AdminShell>
      <div className="space-y-6">
        {/* System Configuration */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              System Configuration
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Current system settings and environment configuration
            </p>
          </div>
          <div className="px-6 py-4">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Application Environment
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      envConfig.billingAlertsMode === "LIVE"
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {envConfig.billingAlertsMode} Mode
                  </span>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Notifications Status
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      envConfig.notificationsConfigured
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {envConfig.notificationsConfigured
                      ? "Configured"
                      : "Not Configured"}
                  </span>
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  API Base URL
                </dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {envConfig.appBaseUrl}
                </dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Database Status
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Connected
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Quick Links
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Access to system configuration pages
            </p>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="/settings/notifications"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-2xl mr-3">🔔</span>
                <div>
                  <div className="font-medium text-gray-900">
                    Notifications Settings
                  </div>
                  <div className="text-sm text-gray-500">
                    Configure Slack and email alerts
                  </div>
                </div>
                <span className="ml-auto text-gray-400">↗</span>
              </a>

              <a
                href="/settings/stripe"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-2xl mr-3">💳</span>
                <div>
                  <div className="font-medium text-gray-900">
                    Stripe Settings
                  </div>
                  <div className="text-sm text-gray-500">
                    Payment processing configuration
                  </div>
                </div>
                <span className="ml-auto text-gray-400">↗</span>
              </a>

              <a
                href="/diag/billing"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-2xl mr-3">📊</span>
                <div>
                  <div className="font-medium text-gray-900">
                    Billing Diagnostics
                  </div>
                  <div className="text-sm text-gray-500">
                    System diagnostics and monitoring
                  </div>
                </div>
                <span className="ml-auto text-gray-400">↗</span>
              </a>

              <a
                href="/status"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-2xl mr-3">🎯</span>
                <div>
                  <div className="font-medium text-gray-900">System Status</div>
                  <div className="text-sm text-gray-500">
                    Deployment readiness and status
                  </div>
                </div>
                <span className="ml-auto text-gray-400">↗</span>
              </a>
            </div>
          </div>
        </div>

        {/* Recent Admin Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Recent Admin Activity
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Audit log of recent administrative actions
            </p>
          </div>
          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No recent activity
              </div>
            ) : (
              <div className="flow-root">
                <ul className="-mb-8">
                  {auditLogs.slice(0, 10).map((log, index) => (
                    <li key={log.id || index}>
                      <div className="relative pb-8">
                        {index !== auditLogs.length - 1 && (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        )}
                        <div className="relative flex space-x-3">
                          <div>
                            <span
                              className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getActionColor(log.action)}`}
                            >
                              <span className="text-xs">
                                {log.action.includes("login")
                                  ? "🔑"
                                  : log.action.includes("logout")
                                    ? "🚪"
                                    : log.action.includes("update")
                                      ? "✏️"
                                      : "📝"}
                              </span>
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">
                                <span className="font-medium text-gray-900">
                                  {log.actor_role === "admin"
                                    ? "Admin"
                                    : "System"}
                                </span>{" "}
                                {log.action.replace(/_/g, " ")}
                                {log.target_type && (
                                  <span> on {log.target_type}</span>
                                )}
                              </p>
                              {log.meta?.email && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {log.meta.email}
                                </p>
                              )}
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              {formatDate(log.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white shadow rounded-lg border border-red-200">
          <div className="px-6 py-4 border-b border-red-200">
            <h3 className="text-lg leading-6 font-medium text-red-900">
              Admin Actions
            </h3>
            <p className="mt-1 text-sm text-red-600">
              Administrative actions and security controls
            </p>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Sign Out (Admin)
                  </h4>
                  <p className="text-sm text-gray-500">
                    Sign out of the admin panel. This will not affect contractor
                    sessions.
                  </p>
                </div>
                <button
                  onClick={handleAdminLogout}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200"
                >
                  Sign Out Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminSettings;
